/**
 * One search result on the shared pocket row: icon, bold match, plain second
 * line, chat menu. A right-click on a chat's row opens the same menu as its ⋯.
 */
import { Fragment, useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
  PocketRow,
} from "@/components/ui";
import { useT } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { HighlightedTitle } from "./highlighted-title";
import { ResultIcon } from "./result-icons";
import type { HubMenuAction, HubResult } from "./hub-types";

/** Stop generating │ Rename… │ Archive: each item its own group. */
function ChatMenu({
  testId,
  title,
  actions,
  open,
  onOpenChange,
}: {
  testId: string;
  title: string;
  actions: HubMenuAction[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT(panelsSearchMessages);
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label={t("actionsFor", { title })}
          data-testid={`${testId}-actions`}
        >
          <Icons.MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-tray-popover="true"
        data-testid={`${testId}-actions-menu`}
      >
        {actions.map((action, index) => (
          <Fragment key={action.id}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={() => void action.onSelect()}
              data-testid={`${testId}-action-${action.id}`}
            >
              {action.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ResultRow({
  result,
  query,
  selected,
  onOpen,
  menu,
}: {
  result: HubResult;
  query: string;
  selected: boolean;
  onOpen: () => void;
  menu: HubMenuAction[] | null;
}) {
  const testId = `hub-row-${result.kind}-${result.id}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const row = (
    <PocketRow
      testId={testId}
      leading={<ResultIcon result={result} />}
      title={<HighlightedTitle text={result.title} query={query} />}
      secondLine={result.secondLine || undefined}
      status={
        result.kind === "task" && result.tone
          ? { tone: result.tone }
          : undefined
      }
      selected={selected}
      onSelect={onOpen}
      hoverActions={
        menu ? (
          <ChatMenu
            testId={testId}
            title={result.title}
            actions={menu}
            open={menuOpen}
            onOpenChange={setMenuOpen}
          />
        ) : undefined
      }
    />
  );
  if (!menu) return row;
  return (
    <div
      className="contents"
      onContextMenu={(event) => {
        // The open menu is portaled: a right-click in it is not the row's.
        if (!event.currentTarget.contains(event.target as Node)) return;
        event.preventDefault();
        setMenuOpen(true);
      }}
      data-testid={`context-${testId}`}
    >
      {row}
    </div>
  );
}
